﻿import { Platform, StyleSheet } from 'react-native';
import type { Theme } from '../../../src/theme/getTheme';
import {
  GROUP_CODE_FLOATING_CLUSTER_PADDING_LEFT,
  GROUP_HERO_BANNER_HERO_FOREGROUND_Z_INDEX,
  GROUP_HERO_BANNER_TV_BADGE_SIZE,
  GROUP_HERO_BANNER_TV_LOGO_IMAGE_SIZE,
  GROUP_HERO_BANNER_HERO_BOTTOM_INSET,
  GROUP_HERO_BANNER_TV_HERO_BOTTOM_PADDING,
  GROUP_HERO_BANNER_TV_MASCOT_HEIGHT,
  GROUP_HERO_BANNER_TV_MASCOT_RESERVE,
  GROUP_HERO_BANNER_TV_MASCOT_WIDTH,
} from './GroupHeroBanner.constants';

export const BANNER_HEIGHT = 180;

interface GroupHeroBannerStyleOptions {
  tvDisplayMode?: boolean;
  tvBannerHeight?: number;
  heroMaxWidth?: number;
  leftScrimWidth?: number;
}

export const getGroupHeroBannerStyles = (
  theme: Theme,
  companyPrimaryColor?: string,
  options?: GroupHeroBannerStyleOptions,
) => {
  const tvDisplayMode = options?.tvDisplayMode ?? false;
  const heroMaxWidth = options?.heroMaxWidth;
  const leftScrimWidth = options?.leftScrimWidth;
  const bannerHeight =
    tvDisplayMode && options?.tvBannerHeight != null ? options.tvBannerHeight : BANNER_HEIGHT;

  return StyleSheet.create({
    banner: {
      height: bannerHeight,
      overflow: 'hidden',
    },
    gradientFill: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
    },
    textureOverlay: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
    },
    orbContainer: {
      position: 'absolute',
      width: 210,
      height: 210,
      top: -80,
      right: -60,
      borderRadius: 105,
      overflow: 'hidden',
    },
    tvDisplayTrailingRow: {
      position: 'absolute',
      top: tvDisplayMode ? 20 : 12,
      right: tvDisplayMode ? 20 : 12,
      zIndex: 20,
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
    },
    tvBlurredLogoBackdrop: {
      position: 'absolute',
      top: -48,
      left: -48,
      right: -48,
      bottom: -48,
      overflow: 'hidden',
      zIndex: 0,
    },
    tvBlurredLogoImage: {
      width: '130%' as never,
      height: '130%' as never,
      opacity: 0.5,
      ...(Platform.OS === 'web' && {
        filter: 'blur(32px)' as never,
        transform: 'scale(1.2)' as never,
      }),
    },
    tvBannerScrim: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.28)',
      zIndex: 1,
    },
    bannerLeftScrim: {
      position: 'absolute',
      top: 0,
      left: 0,
      bottom: 0,
      width: leftScrimWidth ?? '56%',
      zIndex: 2,
    },
    tvHeroRow: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      flexDirection: 'row',
      alignItems: 'flex-end',
      paddingLeft: 36,
      paddingRight: 36,
      paddingBottom: GROUP_HERO_BANNER_TV_HERO_BOTTOM_PADDING,
      zIndex: GROUP_HERO_BANNER_HERO_FOREGROUND_Z_INDEX,
      gap: 32,
    },
    tvHeroRowWithMascot: {
      paddingRight: GROUP_HERO_BANNER_TV_MASCOT_RESERVE,
    },
    tvBadge: {
      position: 'relative',
      bottom: undefined,
      left: undefined,
      width: GROUP_HERO_BANNER_TV_BADGE_SIZE,
      height: GROUP_HERO_BANNER_TV_BADGE_SIZE,
      borderRadius: 28,
      borderWidth: 4,
      borderColor: 'rgba(255,255,255,0.5)',
      backgroundColor: 'rgba(255,255,255,0.14)',
      flexShrink: 0,
    },
    tvBadgeLogo: {
      width: GROUP_HERO_BANNER_TV_BADGE_SIZE,
      height: GROUP_HERO_BANNER_TV_BADGE_SIZE,
      borderRadius: 24,
      borderWidth: 0,
      overflow: 'hidden',
      ...(Platform.OS === 'web' && {
        boxShadow: `0 12px 40px rgba(0,0,0,0.35), 0 0 0 4px ${companyPrimaryColor ?? 'rgba(255,255,255,0.45)'}66` as never,
      }),
    },
    tvBadgeLogoImage: {
      width: GROUP_HERO_BANNER_TV_LOGO_IMAGE_SIZE,
      height: GROUP_HERO_BANNER_TV_LOGO_IMAGE_SIZE,
      borderRadius: 20,
    },
    tvBadgeLogoFallbackText: {
      fontSize: 52,
    },
    tvMascot: {
      bottom: -32,
      right: 20,
      width: GROUP_HERO_BANNER_TV_MASCOT_WIDTH,
      height: GROUP_HERO_BANNER_TV_MASCOT_HEIGHT,
      zIndex: 6,
    },
    tvMascotImage: {
      width: GROUP_HERO_BANNER_TV_MASCOT_WIDTH,
      height: GROUP_HERO_BANNER_TV_MASCOT_HEIGHT,
    },
    tvHeroInfo: {
      flex: 1,
      minWidth: 0,
      paddingBottom: 4,
    },
    tvHeroInfoWithBannerImage: {
      flex: 1,
      flexShrink: 1,
      maxWidth: heroMaxWidth,
    },
    heroInfoWithBannerImage: {
      maxWidth: heroMaxWidth,
    },
    tvTypeBadge: {
      paddingVertical: 6,
      paddingHorizontal: 14,
      marginBottom: 14,
      borderRadius: 24,
    },
    tvTypeBadgeText: {
      fontSize: 12,
      letterSpacing: 1,
    },
    tvGroupName: {
      fontSize: 44,
      lineHeight: 50,
      letterSpacing: -0.5,
      marginBottom: 8,
      paddingRight: 0,
      color: '#FFFFFF',
      ...(Platform.OS === 'web' && {
        textShadow: '0 3px 16px rgba(0,0,0,0.45)' as never,
      }),
    },
    tvGroupMeta: {
      fontSize: 20,
      fontWeight: '600',
      color: 'rgba(255,255,255,0.88)',
      letterSpacing: 0.3,
    },
    /** Dark bar at top for company groups ΓÇö back left; sponsor + group code + share right */
    companyStrip: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: 36,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingLeft: 4,
      paddingRight: 16,
      backgroundColor: 'rgba(0,0,0,0.18)',
      zIndex: 2,
    },
    companyStripTrailing: {
      flex: 1,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'flex-end',
      gap: 8,
      minWidth: 0,
    },
    companyStripSponsor: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
      flexShrink: 1,
      minWidth: 0,
    },
    companyStripLabel: {
      fontSize: 9,
      fontWeight: '700',
      letterSpacing: 0.8,
      color: 'rgba(255,255,255,0.6)',
    },
    companyStripDot: {
      width: 3,
      height: 3,
      borderRadius: 2,
      backgroundColor: 'rgba(255,255,255,0.35)',
    },
    companyStripName: {
      fontSize: 12,
      fontWeight: '800',
      color: 'rgba(255,255,255,0.92)',
      flexShrink: 1,
    },
    backButtonInStrip: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 2,
      paddingHorizontal: 10,
      paddingVertical: 6,
    },
    backButton: {
      position: 'absolute',
      top: 10,
      left: 4,
      flexDirection: 'row',
      alignItems: 'center',
      gap: 2,
      paddingHorizontal: 12,
      paddingVertical: 8,
      zIndex: 3,
    },
    backButtonPressed: {
      opacity: 0.7,
    },
    backButtonHover: {
      opacity: 0.85,
    },
    backLabel: {
      fontSize: 13,
      fontWeight: '700',
      color: 'rgba(255,255,255,0.9)',
    },
    badgeForeground: {
      zIndex: GROUP_HERO_BANNER_HERO_FOREGROUND_Z_INDEX,
    },
    badge: {
      position: 'absolute',
      bottom: GROUP_HERO_BANNER_HERO_BOTTOM_INSET,
      left: 20,
      width: 60,
      height: 60,
      borderRadius: 16,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: 'rgba(255,255,255,0.15)',
      borderWidth: 1.5,
      borderColor: 'rgba(255,255,255,0.3)',
      ...(Platform.OS === 'web' && {
        backdropFilter: 'blur(10px)' as never,
        WebkitBackdropFilter: 'blur(10px)' as never,
        boxShadow: '0 8px 24px rgba(0,0,0,0.25), inset 0 1px 0 rgba(255,255,255,0.3)' as never,
      }),
    },
    badgeLogo: {
      width: 76,
      height: 76,
      borderRadius: 18,
      backgroundColor: 'transparent',
      borderWidth: 0,
      overflow: 'hidden',
      ...(Platform.OS === 'web' && {
        backdropFilter: 'none' as never,
        WebkitBackdropFilter: 'none' as never,
        boxShadow: `0 10px 36px rgba(0,0,0,0.35), 0 0 0 3px ${companyPrimaryColor ?? 'rgba(255,255,255,0.4)'}55` as never,
      }),
    },
    badgeCrest: {
      backgroundColor: 'transparent',
      borderWidth: 0,
      ...(Platform.OS === 'web' && {
        backdropFilter: 'none' as never,
        WebkitBackdropFilter: 'none' as never,
        boxShadow: 'none' as never,
      }),
    },
    badgeEmoji: {
      fontSize: 32,
    },
    badgeLogoImage: {
      width: 76,
      height: 76,
    },
    badgeLogoFallbackText: {
      fontSize: 22,
      fontWeight: '900',
      letterSpacing: -0.5,
      color: companyPrimaryColor ?? theme.primary,
    },
    /** Default / larger screens: prominent mascot, comfortably inset from the right edge. */
    mascot: {
      position: 'absolute',
      bottom: -24,
      right: 16,
      width: 150,
      height: 165,
    },
    /** Small screens: a smaller mascot, tucked to the right edge, so the squad name keeps a usable column. */
    mascotSmall: {
      right: 2,
      width: 122,
      height: 144,
    },
    mascotImage: {
      width: 150,
      height: 165,
    },
    /** Matches mascotSmall so the image scales down with its container on small screens. */
    mascotImageSmall: {
      width: 122,
      height: 144,
    },
    heroInfo: {
      position: 'absolute',
      bottom: GROUP_HERO_BANNER_HERO_BOTTOM_INSET,
      left: 96,
      right: 16,
      zIndex: GROUP_HERO_BANNER_HERO_FOREGROUND_Z_INDEX,
    },
    heroInfoWithLogoUrl: {
      left: 112,
    },
    typeBadge: {
      flexDirection: 'row',
      alignItems: 'center',
      alignSelf: 'flex-start',
      gap: 4,
      backgroundColor: 'rgba(255,255,255,0.18)',
      borderWidth: 1,
      borderColor: 'rgba(255,255,255,0.22)',
      borderRadius: 20,
      paddingVertical: 3,
      paddingHorizontal: 8,
      marginBottom: 7,
      ...(Platform.OS === 'web' && {
        backdropFilter: 'blur(6px)' as never,
        WebkitBackdropFilter: 'blur(6px)' as never,
      }),
    },
    typeBadgeText: {
      fontSize: 9,
      fontWeight: '700',
      letterSpacing: 0.8,
      color: 'rgba(255,255,255,0.9)',
    },
    groupNameOnBannerImage: {
      color: '#FFFFFF',
      // The hero column maxWidth already ends before the mascot, so the name no
      // longer needs its own mascot reservation — dropping it stops the two
      // reservations from stacking and crushing the text on small screens.
      paddingRight: 0,
      ...(Platform.OS === 'web' && {
        textShadow: '0 2px 10px rgba(0,0,0,0.45)' as never,
      }),
    },
    groupMetaOnBannerImage: {
      color: 'rgba(255,255,255,0.88)',
    },
    groupName: {
      fontSize: 22,
      fontWeight: '900',
      color: theme.gray[900],
      // lineHeight must clear descenders (g, y, p) at 900 weight — 24 clipped them below.
      lineHeight: 28,
      letterSpacing: -0.3,
      marginBottom: 4,
      // Ends the name column at the mascot body's left edge so long names never sit under it.
      paddingRight: 146,
      ...(Platform.OS === 'web' && {
        textShadow: '0 2px 8px rgba(0,0,0,0.3)' as never,
      }),
    },
    /** Small screens: smaller mascot frees the column, so the name reclaims part of that space. */
    groupNameSmall: {
      paddingRight: 112,
    },
    groupMeta: {
      fontSize: 11,
      fontWeight: '600',
      color: theme.gray[700],
      letterSpacing: 0.2,
    },
    /**
     * Full-width band under safe top ΓÇö content pinned to the right so pill + share
     * align with the trailing edge (avoids a floating cluster sized to content sitting mid-bar on web).
     */
    groupCodeFloatingCluster: {
      position: 'absolute',
      top: 10,
      left: 0,
      right: 8,
      zIndex: 2,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'flex-end',
      gap: 6,
      paddingLeft: GROUP_CODE_FLOATING_CLUSTER_PADDING_LEFT,
    },
    /** Invite / group code ΓÇö default (non-company) banner: chip next to share */
    groupCodePillFloating: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 5,
      flexShrink: 0,
      paddingVertical: 5,
      paddingHorizontal: 9,
      borderRadius: 10,
      backgroundColor: 'rgba(0,0,0,0.52)',
      borderWidth: 1,
      borderColor: 'rgba(255,255,255,0.32)',
      ...(Platform.OS === 'web' && {
        boxShadow: '0 2px 8px rgba(0,0,0,0.25)' as never,
      }),
    },
    /** Compact chip in company top strip ΓÇö do not shrink label/code into ellipses */
    groupCodePillInStrip: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 4,
      flexShrink: 0,
      paddingVertical: 3,
      paddingHorizontal: 8,
      borderRadius: 8,
      backgroundColor: 'rgba(0,0,0,0.52)',
      borderWidth: 1,
      borderColor: 'rgba(255,255,255,0.32)',
      ...(Platform.OS === 'web' && {
        boxShadow: '0 2px 8px rgba(0,0,0,0.25)' as never,
      }),
    },
    /**
     * Share ΓÇö same glass treatment as the group-code pill so the icon stays readable
     * on light purple gradients and on the company strip.
     */
    shareInviteButton: {
      width: 32,
      height: 32,
      borderRadius: 16,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: 'rgba(0,0,0,0.52)',
      borderWidth: 1,
      borderColor: 'rgba(255,255,255,0.32)',
      flexShrink: 0,
      ...(Platform.OS === 'web' && {
        boxShadow: '0 2px 8px rgba(0,0,0,0.2)' as never,
      }),
    },
    shareInviteButtonPressed: {
      opacity: 0.72,
    },
    shareInviteButtonHover: {
      opacity: 0.9,
    },
    infoButton: {
      width: 32,
      height: 32,
      borderRadius: 16,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: 'rgba(0,0,0,0.52)',
      borderWidth: 1,
      borderColor: 'rgba(255,255,255,0.32)',
      flexShrink: 0,
      ...(Platform.OS === 'web' && {
        boxShadow: '0 2px 8px rgba(0,0,0,0.2)' as never,
      }),
    },
    infoButtonPressed: {
      opacity: 0.72,
    },
    infoButtonHover: {
      opacity: 0.9,
    },
    groupCodePillPressed: {
      opacity: 0.75,
    },
    groupCodePillHover: {
      opacity: 0.92,
    },
    groupCodeLabel: {
      fontSize: 8,
      fontWeight: '800',
      letterSpacing: 0.7,
      color: 'rgba(255,255,255,0.88)',
      textTransform: 'uppercase',
    },
    groupCodeValue: {
      fontSize: 12,
      fontWeight: '900',
      letterSpacing: 0.6,
      color: 'rgba(255,255,255,0.98)',
      fontVariant: ['tabular-nums'],
    },
    groupCodeValueStrip: {
      fontSize: 11,
      fontWeight: '900',
      letterSpacing: 0.5,
      color: 'rgba(255,255,255,0.98)',
      fontVariant: ['tabular-nums'],
    },
  });
};
